import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ProfileModal from '../components/ProfileModal'; // Import the new component
import api from '../utils/axios';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Edit,
  Camera,
  Building,
  Palette,
  Share2,
  Image as ImageIcon,
  Globe,
  Clock,
  Star,
  Settings,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Calendar,
} from 'lucide-react';

const Profile = () => {
  const { user, vendor, updateVendorData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];


const [businessHours, setBusinessHours] = useState(() => {
  const init = {};
  days.forEach((day) => {
    init[day] = {
      open: '',
      close: '',
      closed: false,
    };
  });
  return init;
});


const fetchBusinessHours = async () => {
  try {
    const res = await api.get('/vendor/business-hours');
    const backendHours = res.data.businessHours;

    const toTime = (t) => (t ? t.slice(0, 5) : '');

    const formatted = {};

    Object.keys(backendHours).forEach((day) => {
      const Day = day.charAt(0).toUpperCase() + day.slice(1);

      const isClosedByTime =
        backendHours[day].open === '00:00:00' &&
        backendHours[day].close === '00:00:00';

      formatted[Day] = {
        open: toTime(backendHours[day].open),
        close: toTime(backendHours[day].close),
        closed: isClosedByTime || !backendHours[day].isOpen,
      };
    });

    setBusinessHours((prev) => ({ ...prev, ...formatted }));
  } catch (err) {
    console.error('Failed to fetch business hours', err);
  }
};

  useEffect(() => {
  if (activeTab === 'business-hours') {
    fetchBusinessHours();
  }
}, [activeTab]);



  const handleEditProfile = () => {
    setIsEditing(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const handleUpdateBusinessHours = async () => {
  try {
    const payload = { businessHours: {} };

    days.forEach((day) => {
      const key = day.toLowerCase();
      const data = businessHours[day];

      const isClosedByTime =
        data.open === '00:00' && data.close === '00:00';

      payload.businessHours[key] = {
        open: data.closed ? null : data.open,
        close: data.closed ? null : data.close,
        isOpen: !data.closed && !isClosedByTime,
      };
    });

    await api.put('/vendor/business-hours', payload);

    // 🔥 IMMEDIATE UI REFRESH
    await fetchBusinessHours();
  } catch (error) {
    console.error('Failed to update business hours', error);
    alert('Failed to update business hours');
  }
};




  const handleProfileUpdateSuccess = () => {
    setIsEditing(false);
  };

  return (
    <div className=" ">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">
            Restaurant Profile
          </h1>
          <p className="text-secondary-600">
            Manage your restaurant information and settings
          </p>
        </div>
        <button
          onClick={handleEditProfile}
          className="btn-primary flex items-center"
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Profile
        </button>
      </div>

      {/* Profile Overview */}
      <div className="card">
        <div className="flex items-start space-x-6">
          {/* Restaurant Logo */}
          <div className="relative">
            <div className="h-24 w-24 bg-secondary-100 rounded-lg flex items-center justify-center overflow-hidden">
              {vendor?.logo_url ? (
                <img
                  src={vendor.logo_url}
                  alt={vendor.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Building className="h-12 w-12 text-secondary-400" />
              )}
            </div>
            <button className="absolute -bottom-1 -right-1 h-8 w-8 bg-primary-500 rounded-full flex items-center justify-center text-white hover:bg-primary-600 transition-colors">
              <Camera className="h-4 w-4" />
            </button>
          </div>

          {/* Restaurant Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-xl font-semibold text-secondary-900">
                {vendor?.name}
              </h2>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  vendor?.status
                )}`}
              >
                {getStatusIcon(vendor?.status)}
                <span className="ml-1 capitalize">{vendor?.status}</span>
              </span>
            </div>

            {vendor?.description && (
              <p className="text-secondary-600 mb-3">{vendor.description}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center text-secondary-600">
                <MapPin className="h-4 w-4 mr-2" />
                <span>{vendor?.location || 'Location not set'}</span>
              </div>
              <div className="flex items-center text-secondary-600">
                <Phone className="h-4 w-4 mr-2" />
                <span>{vendor?.phone || 'Phone not set'}</span>
              </div>
              <div className="flex items-center text-secondary-600">
                <Star className="h-4 w-4 mr-2" />
                <span>{vendor?.rating || '0'} / 5.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-secondary-200">
          <nav className="flex space-x-8">
            {[
              { id: 'general', label: 'General Info', icon: User },
              { id: 'branding', label: 'Branding', icon: Palette },
              { id: 'social', label: 'Social Links', icon: Share2 },
              { id: 'settings', label: 'Settings', icon: Settings },
              { id: 'business-hours', label: 'Business Hours', icon: Clock },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="py-6">
          {activeTab === 'general' && (
            <div className=" ">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Restaurant Name
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <User className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">{vendor?.name}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Email Address
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Mail className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">{vendor?.email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Phone Number
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Phone className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.phone || 'Not set'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Location
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <MapPin className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.location || 'Not set'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Description
                </label>
                <div className="p-3 bg-secondary-50 rounded-lg">
                  <p className="text-secondary-900">
                    {vendor?.description || 'No description provided'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className=" ">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Theme Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <div
                      className="h-10 w-10 rounded-lg border-2 border-secondary-200"
                      style={{
                        backgroundColor: vendor?.theme_color || '#FF6B35',
                      }}
                    />
                    <span className="text-secondary-900 font-mono">
                      {vendor?.theme_color || '#FF6B35'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Promo Message
                  </label>
                  <div className="p-3 bg-secondary-50 rounded-lg">
                    <p className="text-secondary-900">
                      {vendor?.promo_message || 'No promo message set'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Logo URL
                  </label>
                  <div className="p-3 bg-secondary-50 rounded-lg">
                    <p className="text-secondary-900 break-all">
                      {vendor?.logo_url || 'No logo URL set'}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Banner Image URL
                  </label>
                  <div className="p-3 bg-secondary-50 rounded-lg">
                    <p className="text-secondary-900 break-all">
                      {vendor?.banner_image_url || 'No banner URL set'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'social' && (
            <div className=" ">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Facebook
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Globe className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.social_links?.facebook || 'Not connected'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Instagram
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Globe className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.social_links?.instagram || 'Not connected'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Twitter
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Globe className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.social_links?.twitter || 'Not connected'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Website
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Globe className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.social_links?.website || 'Not set'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className=" ">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Account Status
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    {getStatusIcon(vendor?.status)}
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        vendor?.status
                      )}`}
                    >
                      {vendor?.status?.charAt(0).toUpperCase() +
                        vendor?.status?.slice(1)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Verification Status
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    {vendor?.is_verified ? (
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-yellow-500 mr-2" />
                    )}
                    <span className="text-secondary-900">
                      {vendor?.is_verified ? 'Verified' : 'Not Verified'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Rating
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Star className="h-4 w-4 text-yellow-500 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.rating || '0'} / 5.0
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Price Category
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <DollarSign className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900 capitalize">
                      {vendor?.price_category?.replace('_', ' ') || 'Not set'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Preparation Time
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Clock className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.preparation_time_min || '0'} -{' '}
                      {vendor?.preparation_time_max || '0'} minutes
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Member Since
                  </label>
                  <div className="flex items-center p-3 bg-secondary-50 rounded-lg">
                    <Calendar className="h-4 w-4 text-secondary-400 mr-2" />
                    <span className="text-secondary-900">
                      {vendor?.created_at
                        ? new Date(vendor.created_at).toLocaleDateString()
                        : 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
         {activeTab === 'business-hours' && (
  <div>
    <h2 className="text-lg font-semibold text-secondary-900 mb-4">
      Business Hours
    </h2>

    <div className="overflow-x-auto">
      <table className="w-full border border-secondary-200 rounded-lg">
        <thead className="bg-secondary-50">
          <tr>
            <th className="px-4 py-3 text-left">Day</th>
            <th className="px-4 py-3 text-left">Opening</th>
            <th className="px-4 py-3 text-left">Closing</th>
            <th className="px-4 py-3 text-left">Status</th>
          </tr>
        </thead>

        <tbody className="divide-y">
  {days.map((day) => {
    const dayData = businessHours[day];

    return (
      <tr key={day}>
        <td className="px-4 py-3 font-medium">{day}</td>

        {/* OPEN */}
        <td className="px-4 py-3">
          <input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={dayData.open}
            disabled={dayData.closed}
            onChange={(e) => {
              let v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
              if (v.length > 2) v = v.slice(0, 2) + ':' + v.slice(2);

              setBusinessHours((prev) => ({
                ...prev,
                [day]: { ...prev[day], open: v },
              }));
            }}
            className="input"
          />
        </td>

        {/* CLOSE */}
        <td className="px-4 py-3">
          <input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={dayData.close}
            disabled={dayData.closed}
            onChange={(e) => {
              let v = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
              if (v.length > 2) v = v.slice(0, 2) + ':' + v.slice(2);

              setBusinessHours((prev) => ({
                ...prev,
                [day]: { ...prev[day], close: v },
              }));
            }}
            className="input"
          />
        </td>

        {/* STATUS */}
        <td className="px-4 py-3">
          <button
            onClick={() =>
              setBusinessHours((prev) => ({
                ...prev,
                [day]: {
                  ...prev[day],
                  closed: !prev[day].closed,
                },
              }))
            }
            className={`px-3 py-1 text-xs rounded-full ${
              dayData.closed
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}
          >
            {dayData.closed ? 'Closed' : 'Open'}
          </button>
        </td>
      </tr>
    );
  })}
</tbody>
      </table>
    </div>

    <div className="flex justify-end mt-6">
      <button
        className="btn-primary"
        onClick={handleUpdateBusinessHours}
      >
        Update Timing
      </button>
    </div>
  </div>
)}

        </div>
      </div>
     {isEditing && (
        <ProfileModal
          vendor={vendor}
          onClose={() => setIsEditing(false)}
          onSuccess={handleProfileUpdateSuccess}
          updateVendorData={updateVendorData}
        />
      )}
    </div>
  );
};

export default Profile;